import { useContext, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ActionGridButton from 'components/ActionGridButton/ActionGridButton'
import { WppButton, WppSkeleton, WppTypography } from '@wppopen/components-library-react'
import usePermissions from 'hooks/usePermissions'
import useInitialiser from 'hooks/useInitialiser'
import { pluralise } from 'utils'
import useApi from 'hooks/useApi'
import appContext from 'context/appContext'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'
import { BANNER_KEYS, PDP_GENERATOR_API_HEADERS } from 'utils/constants'
import ImportEditClientModal from 'components/Modals/ImportEditClientModal/ImportEditClientModal'
import type { ClientsListDataV5 } from 'utils/utils.d'
import type { ClientsCreatePayload } from '@contracts'
import type { FEApiError } from 'utils/errorHandling'
import settingsStyles from '../SettingsPage.module.scss'
import actionGridStyles from 'components/ActionGridButton/ActionGridButton.module.scss'

interface ClientsViewProps {
  clientsData: ClientsListDataV5 | null
}

export default function ClientsView({ clientsData }: ClientsViewProps) {
  const navigate = useNavigate()
  const { setToastDetailsContext, setActiveBannerId } = useContext(SettingsPageContext)
  const { fetchClientData, isClientApiLoading } = useContext(appContext)
  const [isImportClientModalOpen, setIsImportClientModalOpen] = useState(false)
  const { clientsWithPermissions } = usePermissions({
    permissionSubset: ['PDP_APP_POST_CLIENTS', 'PDP_APP_GET_CLIENTS', 'PDP_APP_POST_BRANDS'],
  })
  const [clientsWithCreateClient, viewableClientIds] = clientsWithPermissions
  const canPostClient = !!clientsWithCreateClient?.length

  const {
    fetchData: postImportClient,
    isLoading: isPostImportClientLoading,
    url: postImportClientUrl,
  } = useApi('clients')

  const gridContainerElementRef = useRef<HTMLDivElement | null>(null)
  const { isInitialising } = useInitialiser({})

  const clientsActionGridEls = useMemo(() => {
    if (!clientsData || !clientsData.clients?.length || !viewableClientIds?.length)
      return <WppTypography>No clients available</WppTypography>

    const clientActionGridEls = clientsData.clients.flatMap(
      ({ id, client_name, client_logo_url, brands, created_at }) => {
        if (created_at === null || !viewableClientIds?.includes(id!)) return []

        return [
          <ActionGridButton
            key={id}
            title={client_name}
            image={client_logo_url}
            subTitle={`${brands?.length} ${pluralise('Brand', brands?.length)}`}
            onBtnClick={() => navigate(`/settings/client/${id}`)}
            isClient
          />,
        ]
      },
    )

    return clientActionGridEls
  }, [clientsData, isInitialising])

  const loadingSkeletons = useMemo(() => {
    return isInitialising
      ? new Array(canPostClient ? 5 : 6)
          .fill('')
          .map((_, idx) => (
            <WppSkeleton
              variant="rectangle"
              className={actionGridStyles.skeleton}
              data-testid="client-grid-button-skeleton"
              key={idx}
            />
          ))
      : null
  }, [isInitialising])

  const onImportClient = async (data: ClientsCreatePayload) => {
    try {
      if (!canPostClient) throw new Error('You do not have permission to import clients')

      await postImportClient(postImportClientUrl, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: data.id as string },
      })

      await fetchClientData()

      setActiveBannerId(BANNER_KEYS.SETTINGS_HOME)
      setToastDetailsContext({
        type: 'success',
        message: 'Client imported successfully',
      })

      setIsImportClientModalOpen(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  return (
    <>
      {canPostClient && (
        <div className={settingsStyles.importClient}>
          <WppButton onClick={() => setIsImportClientModalOpen(true)}>Import client</WppButton>
          {isImportClientModalOpen && (
            <ImportEditClientModal
              onCancel={() => setIsImportClientModalOpen(false)}
              isModalOpen={isImportClientModalOpen}
              isApiLoading={isPostImportClientLoading || isClientApiLoading}
              onSubmit={onImportClient}
            />
          )}
        </div>
      )}
      <div className={settingsStyles.grid} ref={gridContainerElementRef} data-testid="clients-grid">
        {isInitialising ? loadingSkeletons : clientsActionGridEls}
      </div>
    </>
  )
}
